import './tokens-extra.css';
import { clsx } from 'clsx';
import { type KeyboardEvent, type MouseEvent, type ReactNode, type SyntheticEvent, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { dialogClosed, dialogOpened } from './topLayer';

/**
 * The shared base of Sheet and Modal: a native <dialog> opened with showModal(), portaled to
 * <body>. That gives the top layer, an inert page behind, Escape, and focus trapping for free.
 * Without showModal (jsdom, very old WebViews) it falls back to an open dialog with its own scrim.
 * Mounted only while open.
 */
export interface TopDialogProps {
  onClose: () => void;
  /** Escape, backdrop tap and swipe close it. Default true. */
  dismissible?: boolean;
  labelledBy: string;
  describedBy?: string;
  className?: string;
  children: ReactNode;
}

export function TopDialog({
  onClose,
  dismissible = true,
  labelledBy,
  describedBy,
  className,
  children,
}: TopDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let fallback = false;
    if (!d.open) {
      if (typeof d.showModal === 'function') {
        try {
          d.showModal();
        } catch {
          fallback = true;
        }
      } else fallback = true;
      if (fallback) {
        d.setAttribute('open', '');
        d.dataset.fallback = '';
        d.querySelector<HTMLElement>('[autofocus], button, input, [tabindex]')?.focus();
      }
    }
    dialogOpened();
    return () => {
      dialogClosed();
      if (typeof d.close === 'function' && !fallback) d.close();
      else d.removeAttribute('open');
      if (before?.isConnected) before.focus();
    };
  }, []);

  const onCancel = (e: SyntheticEvent) => {
    e.preventDefault();
    if (dismissible) closeRef.current();
  };

  const onBackdrop = (e: MouseEvent<HTMLDialogElement>) => {
    if (dismissible && e.target === e.currentTarget) closeRef.current();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDialogElement>) => {
    // The fallback dialog gets no native cancel event.
    if (e.key === 'Escape' && ref.current?.dataset.fallback !== undefined) onCancel(e);
  };

  return createPortal(
    <dialog
      ref={ref}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-modal="true"
      className={clsx('tts-ui-dialog', className)}
      onCancel={onCancel}
      onClick={onBackdrop}
      onKeyDown={onKeyDown}
    >
      {children}
    </dialog>,
    document.body,
  );
}
